<script setup>
import axios from 'axios';
import { onMounted, ref, watch } from 'vue';
import { validateSpecialCharacter } from '../../../../helpers/helper';
import PgeButton from '../../../../pge-components/pge-button.vue';
import PgeInputText from '../../../../pge-components/pge-input-text.vue';
import { useEventStore } from '../../../../stores/event';
import usePasswordMatch, { PASSWORD_HINT, meetsPasswordRule } from '../../../../composables/usePasswordMatch';
import AuthTermsNotice from '../AuthTermsNotice.vue';
import { stashRegistration } from '../../../../helpers/pendingRegistration';

const props = defineProps({
	eventData: Object,
	changeFormStep: Function,
	currentFormStepData: {
		type: Object,
		default: () => ({}),
	},
});

const { event } = useEventStore();
const isLoading = ref(false);
const allowSubmit = ref(false);
const errors = ref([]);
const turnstileToken = ref(false);
const firstnameLimitExceedMessage = ref(null);
const lastnameLimitExceedMessage = ref(null);
const userFormData = ref({
	email: props.currentFormStepData.user?.email || props.currentFormStepData.email || '',
	firstname: null,
	lastname: null,
	password: '',
	rePassword: '',
});
// The button stays grey until the two agree; this is what says why.
const { mismatch, onConfirmationFocus } = usePasswordMatch(
	() => userFormData.value.password,
	() => userFormData.value.rePassword,
);

const hasCustomFields = !!(props.eventData.settings.fields && props.eventData.settings.fields.length);

async function handleSubmit() {
	if (!allowSubmit.value || isLoading.value) return;

	isLoading.value = true;
	errors.value = [];

	try {
		const res = await axios.post('/api/sso/api/user-create', {
			first_name: userFormData.value.firstname,
			last_name: userFormData.value.lastname,
			email: userFormData.value.email,
			password: userFormData.value.password,
			password_confirmation: userFormData.value.rePassword,
			subdomain: window.store.subdomain,
			logo: event.value.login_logo,
			'cf-turnstile-response': turnstileToken.value,
		});

		if (res.data.errors) {
			errors.value = Array.isArray(res.data.errors) ? res.data.errors : Object.values(res.data.errors).flat();
			loadCaptcha();
		} else if (res.data.success) {
			const ssoUser = {
				id: res.data.data.id,
				sso_user_id: res.data.data.id,
				email: userFormData.value.email,
				first_name: userFormData.value.firstname,
				last_name: userFormData.value.lastname,
			};

			if (hasCustomFields) {
				// Custom fields configured → collect them next, then verify.
				props.changeFormStep('custom-fields', { user: ssoUser, isNewUser: true });
			} else {
				// No custom fields → registered here once verification signs them in.
				stashRegistration(ssoUser.email);
				props.changeFormStep('verify-code', { email: ssoUser.email });
			}
		} else {
			errors.value = ['Something went wrong, please try again later!'];
			loadCaptcha();
		}
	} catch (error) {
		errors.value = [error.response?.data?.message || 'Something went wrong, please try again later!'];
		loadCaptcha();
	} finally {
		isLoading.value = false;
	}
}

function loadCaptcha() {
	const _env = window.location.hostname.split('.');
	if (!['qa', 'local'].includes(_env[1])) return;

	const script = document.createElement('script');
	script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
	script.async = true;
	script.onload = () => {
		if (document.querySelector('#turnstile-container')) {
			document.querySelector('#turnstile-container').innerHTML = '';
		}

		window.turnstile.render(document.querySelector('#turnstile-container'), {
			theme: 'light',
			appearance: 'interaction-only',
			sitekey: import.meta.env.VITE_TURNSTILE_SITEKEY,
			callback: function (token) {
				turnstileToken.value = token;
			},
		});
	};
	document.head.appendChild(script);
}

function validateForm(newVal) {
	let _allowSubmit = true;
	firstnameLimitExceedMessage.value = false;
	lastnameLimitExceedMessage.value = false;

	if (newVal.firstname) {
		if (newVal.firstname.length > 50) {
			_allowSubmit = false;
			firstnameLimitExceedMessage.value = 'Character limit for First Name should be 50 max';
		}
		if (validateSpecialCharacter(newVal.firstname)) {
			_allowSubmit = false;
			firstnameLimitExceedMessage.value = "Special characters aren't allowed";
		}
	} else if (newVal.firstname !== null) {
		_allowSubmit = false;
		firstnameLimitExceedMessage.value = 'Cannot be blank';
	}

	if (newVal.lastname) {
		if (newVal.lastname.length > 50) {
			_allowSubmit = false;
			lastnameLimitExceedMessage.value = 'Character limit for Last Name should be 50 max';
		}
		if (validateSpecialCharacter(newVal.lastname)) {
			_allowSubmit = false;
			lastnameLimitExceedMessage.value = "Special characters aren't allowed";
		}
	} else if (newVal.lastname !== null) {
		_allowSubmit = false;
		lastnameLimitExceedMessage.value = 'Cannot be blank';
	}

	if (!newVal.email) _allowSubmit = false;
	if (!newVal.firstname) _allowSubmit = false;
	if (!newVal.lastname) _allowSubmit = false;

	if (newVal.password && newVal.rePassword) {
		if (meetsPasswordRule(newVal.password)) {
			if (newVal.password !== newVal.rePassword) _allowSubmit = false;
		} else {
			_allowSubmit = false;
		}
	} else {
		_allowSubmit = false;
	}

	allowSubmit.value = _allowSubmit;
}

watch(userFormData, newVal => {
	validateForm(newVal);
}, { deep: true });

onMounted(() => {
	loadCaptcha();
});
</script>

<template>
	<div class="form">
		<div class="form__title">New account details</div>
		<div class="bg-white form__inline">
			<pge-input-text type="text" name="firstname" label="First Name" :model="userFormData.firstname"
				v-model="userFormData.firstname" required has-required-sign no-tabindex
				:should-show-error="firstnameLimitExceedMessage" :error-message="firstnameLimitExceedMessage"
				:has-error="firstnameLimitExceedMessage ? true : false" />
			<pge-input-text type="text" name="lastname" label="Last Name" :model="userFormData.lastname"
				v-model="userFormData.lastname" required has-required-sign no-tabindex
				:should-show-error="lastnameLimitExceedMessage" :error-message="lastnameLimitExceedMessage"
				:has-error="lastnameLimitExceedMessage ? true : false" />
		</div>
		<div class="bg-white form__inline">
			<pge-input-text type="password" name="password" label="Password" :model="userFormData.password"
				v-model="userFormData.password" required has-required-sign has-trailing-icon no-tabindex min-length="12"
				:should-show-error="!!userFormData.password && !meetsPasswordRule(userFormData.password)" :error-message="PASSWORD_HINT" />
			<pge-input-text type="password" name="rePassword" label="Confirm Password" :model="userFormData.rePassword"
				v-model="userFormData.rePassword" required has-required-sign has-trailing-icon no-tabindex
				:has-error="!!mismatch" :error-message="mismatch"
				@update:focused="onConfirmationFocus" />
		</div>
		<div class="bg-white">
			<pge-input-text type="email" name="email" label="Email" :model="userFormData.email"
				v-model="userFormData.email" required has-required-sign no-tabindex />
		</div>
		<div id="turnstile-container"></div>
		<div class="form__actions">
			<pge-button label="Back" template="text" @click="() => props.changeFormStep('check-email')" />
			<pge-button label="Create Account" @click="handleSubmit" :disabled="!allowSubmit || isLoading" />
		</div>
		<div class="form__errors" v-if="errors.length">
			<div v-for="error in errors" :key="error">{{ error }}</div>
		</div>
		<AuthTermsNotice :links="eventData?.settings?.links || []" />
	</div>
</template>

<style lang="scss" scoped>
.form {
	display: flex;
	flex-direction: column;
	gap: 22px;

	&__title {
		text-align: center;
		font-weight: 600;
		font-size: 18px;
		line-height: 1.5;
		color: #000;
	}

	&__inline {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 15px;
	}

	&__actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}

	&__errors {
		display: flex;
		flex-direction: column;
		gap: 10px;

		div {
			text-align: center;
			color: #f83c3c;
			font-weight: 700;
			font-size: 14px;
			line-height: 20px;
		}
	}
}

.bg-white {
	background: white;
}
</style>
